import React from 'react';
import { connect } from 'react-redux';
import { Box, TextInput, Button, Paragraph, Text, ResponsiveContext, Anchor, Layer, Accordion, AccordionPanel, Image } from 'grommet';
import { TabContent, TabPane, Nav, NavItem, NavLink } from 'reactstrap';
import { RouteComponentProps, withRouter } from 'react-router';
import { ApplicationState } from '../store';
import { retrieveVideo, videoSlice } from '../store/Video';
import { performSearch, searchSlice, defaultSortOrder } from '../store/Search';
import { RelatedLink } from '../models/RelatedLink';
import { Video as VideoModel } from '../models/Video';
import { VideoInfo } from '../models/VideoInfo';
import SummaryCard from '../components/SummaryCard';
import { Search } from "grommet-icons";
import { push } from 'connected-react-router-for-react18';
import classnames from 'classnames';
import { Helmet } from "react-helmet";
import { isStaging, isProduction, isPreview, setupMetrics, setupRecommendations, setupPlayer } from "../app/utilities"
import parse from 'html-react-parser';
import { BrightcoveReactPlayer } from '../components//BrightCove/BrightcoveReactPlayer';
import { loadScript } from '../app/scriptLoader';
import queryString from 'query-string';
import _ from 'lodash';
import { checkRedirect} from '../RedirectLoader';

interface IBrightcoverPlayerProps {
    videoJsOptions?: any;
    video: VideoModel;
    max: boolean;
    embedId?: string;
    autoplay?: boolean;
}

interface VideoWindowInterface extends Window {
    recommendationWidget: any;
}

declare let window: VideoWindowInterface;

const mapDispatchToProps = {
    push,
    retrieveVideo,
    performSearch,
    ...searchSlice.actions,
    ...videoSlice.actions
}

interface IVideoState {
    activeTab: number;
    activePlayList: number | undefined;
    sizeRestored: boolean;
    activeTabSet: boolean;
    hpeScriptLoaded: boolean;
}

type VideoProps =
    ApplicationState // ... state we've requested from the Redux store
    & typeof mapDispatchToProps
    & RouteComponentProps<any>; // ... plus incoming routing parameters

class VideoPage extends React.PureComponent<VideoProps, IVideoState> {
    searchInput: any;

    constructor(props: VideoProps) {
        super(props);
        this.state = {
            activeTab: 0,
            activePlayList: 0,
            sizeRestored: false,
            activeTabSet: false,
            hpeScriptLoaded: false
        };
        // let keys=['', '', '', '', ''];
        this.searchInput = React.createRef();
        this.props.retrieveVideo(this.props.match.params.id);
    }

    public componentDidMount() {
    }

    public componentDidUpdate() {
        let isMax = this.props.match.params.max !== undefined && this.props.match.params.max === 'max';
        let video = this.props.video.video;

        if (video && !this.state.hpeScriptLoaded) {
            setupMetrics(video?.name, isMax);
            this.setState({ hpeScriptLoaded: true })
        }
        if (this.state.activeTab === 8 && this.searchInput.current) {
            this.searchInput.current.focus();
        }
    }

    onSuccess = (success: any) => {
        let isMax = this.props.match.params.max !== undefined && this.props.match.params.max === 'max';
        let video = this.props.video.video;
        let loc = window.location.protocol;
        let overlayUrl = `${loc}//players.brightcove.net/videojs-overlay/2/videojs-overlay.min.js`;
        let player = success.ref;
        let currentQuery = queryString.parse(this.props.location.search, { parseBooleans: true });
        let autoplay = currentQuery.autoplay ? JSON.parse(String(currentQuery.autoplay)) : false;

        loadScript('overlayScript', overlayUrl);

        setTimeout(() => {
            setupPlayer(player, video, isMax, autoplay);
        }, 100);
    }

    onFailure = (failure: any) => {
        // The player object or iframe element (depending on embed type) can be
        // accessed in two ways.
        //
        // From the success object passed to the `onFailure` callback:
        console.log(failure.message, failure.stack);
    };

    setActiveTab = (tab: number) => {
        if (this.state.activeTab !== tab) {
            this.setState({ activeTab: tab });
        }
    }

    getPlayerOptions = (video: VideoModel) => {
        let currentQuery = queryString.parse(this.props.location.search, { parseBooleans: true });
        let autoplay = currentQuery.autoplay ? currentQuery.autoplay : false;

        let brightcoveOptions: IBrightcoverPlayerProps = {
            autoplay: autoplay ? true : false,
            video: video,
            max: this.props.match.params.max !== undefined
        };

        return brightcoveOptions
    }

    onKeywordChange = (event: any) => {
        if (event.key === 'Enter') {
            this.performSearch();
        }
        else {
            this.props.setKeyword(event.target.value);
        }
    }

    performAdvancedSearch = () => {
        this.props.setVideo(undefined);
        this.props.push("/", this.props.search?.request);
    }

    performSearch = () => {
        this.props.setSortOrder(defaultSortOrder);
        this.props.setPageSize(10);
        this.props.performSearch();
    }

    gotoVideo = (info: VideoInfo) => {
        this.props.push(info.href);
        this.props.retrieveVideo(info.id);
    }

    gotoUrl = (url: string) => {
        window.open(url, "_blank");
    }

    getMetatags = () => {
        let video = this.props.video.video;
        let isProd = isProduction();
        let metaTags = video?.metaTags.map((tag, index) =>
            <meta name={tag.key} key={`video-meta-${index}`} content={tag.value} />
        );

        if (!isProd || !video?.isListed) {
            metaTags = _.filter(metaTags, tag => tag.props.name !== "robots");
        }
        if (!video?.isListed) {
            metaTags?.push(<meta name="robots" content="noindex" />)
        }
        if (!isProd && video?.isListed) {
            metaTags?.push(<meta name="robots" content="nofollow, noindex" />)
        }

        return metaTags;
    }

    onPlaylistChange = (activePanels: number[]) => {
        if (activePanels.length !== 0) {
            this.setState({ activePlayList: activePanels[0] });
        }
        else {
            this.setState({ activePlayList: -1 });
        }
    }

    renderVideo = (isMax: boolean, size: string) => {
        let video = this.props.video.video;
        let location = window.location;
        let applicationInfo = location.pathname.match(/^\/\w+-\w+/gi);
        let rootUrl = `${location.origin}${applicationInfo == null ? location.pathname : applicationInfo[0]}`;
        let currentUrl = decodeURIComponent(location.pathname);
        let newUrl = video?.href ?? currentUrl;
        let audioLanguages = video && video.audioLanguages?.map((audioLanguage, ndx) => {
            return <Text size='xsmall' key={`al-${ndx}`}>{audioLanguage}</Text>;
        });

        newUrl = isMax && newUrl.indexOf('max') === -1 ? `${newUrl}max` : newUrl;
        if ((video?.name !== 'NOT_AVAILABLE') && (video?.name !== 'PLAYER_ERR_NO_LONGER_AVAILABLE') &&
            (video?.name !== 'PLAYER_ERR_NOT_YET_AVAILABLE') &&
            (newUrl !== currentUrl) && (currentUrl.indexOf('/video/') !== -1) && (currentUrl.indexOf(newUrl) === -1)) {
            console.log(`location: ${rootUrl}`)
            console.log(`currentUrl: ${currentUrl}`)
            console.log(`newUrl: ${newUrl}`)

            // newUrl = `${newUrl}${location.search}`;
            setTimeout(() => {
                this.props.history.replace({
                    pathname: newUrl,
                    search: location.search
                });
            }, 200);
        }
        return <Box fill align="center" gap="none">
            {
                video && <Box fill gap="none" >
                    {(video?.name !== 'NOT_AVAILABLE') && (video?.name !== 'PLAYER_ERR_NO_LONGER_AVAILABLE') &&
                        (video?.name !== 'PLAYER_ERR_NOT_YET_AVAILABLE') && <Helmet>
                            <meta name="uaid" content={`${video.uaid}`} />
                            <meta name="image" property="og:image" content={`${video.posterImage}`} />
                            <meta property="og:type" content="video.other" />
                            <meta property="og:title" content={`${video.name}`} />
                            <meta property="og:site_name" content="HPE Video Gallery" />
                            <meta property="og:url" content={`${video.href}`} />
                            <meta property="og:description" content={`${video.description}`} />
                            <meta property="og:locale" content={`${video.language}`} />
                            <meta name="twitter:card" content="summary_large_image" />
                            <meta name="version" content={`${video.version}`} />
                            <meta name="description" content={`${video.description}`} />
                            {this.getMetatags()}
                            <title>{video.name}</title>
                        </Helmet>
                    }
                    <Box gap="none" className={isMax ? "video-max" : "vid-container"} background="dark-2">
                        {
                            (video?.name !== 'NOT_AVAILABLE') && (video?.name !== 'PLAYER_ERR_NO_LONGER_AVAILABLE') &&
                                (video?.name !== 'PLAYER_ERR_NOT_YET_AVAILABLE')
                                ? <Box fill>
                                    <BrightcoveReactPlayer onFailure={this.onFailure} onSuccess={this.onSuccess} {...this.getPlayerOptions(video)} />
                                </Box>
                                : ((video?.name === 'PLAYER_ERR_NO_LONGER_AVAILABLE') || (video?.name === 'PLAYER_ERR_NOT_YET_AVAILABLE'))
                                    ? <Box className="vid-player" justify="center" align="center" border="all">
                                        <Text size="xlarge" color="status-critical" weight="bold">{video.description}.</Text>
                                    </Box>
                                    : <Box className="vid-player" justify="center" align="center" border="all">
                                        <Text size="xlarge" color="status-critical" weight="bold">Sorry, the video you're looking for isn't here.</Text>
                                        <Text size="small" color="light-4">It may have been moved to a new location or removed from our system.<br />Please double check the URL and try again.</Text>
                                    </Box>
                        }
                    </Box>
                    {!isMax && (video?.name !== 'NOT_AVAILABLE') && (video?.name !== 'PLAYER_ERR_NO_LONGER_AVAILABLE') &&
                        (video?.name !== 'PLAYER_ERR_NOT_YET_AVAILABLE') &&
                        <Box gap="xsmall" pad={{ left: "xsmall", right: "xsmall", bottom: "small" }}>
                            <Box direction='row' fill='horizontal' gap="xsmall" justify='between' pad={{ bottom: 'small', right: 'small', left: "none" }}>
                                <Box direction='row' gap="small">
                                    <Text size='xsmall'>[{video.length}]</Text>
                                    <Box gap="none">
                                        {audioLanguages}
                                    </Box>
                                </Box>
                                <Text size='xsmall'>Uploaded {video.uploadDate}</Text>
                                <Text size='xsmall'>{video.views} View{video.views > 1 ? 's' : ''}</Text>
                            </Box>
                            <Box pad={{ bottom: "small" }}>
                                <Text weight='bold' size={(size === "medium_small") ? "small" : size}>{video?.name}</Text>
                            </Box>
                            {(video.longDescription || video.description) &&
                                <Box fill='horizontal'>
                                    <Paragraph fill>
                                        {(video.longDescription || video.description).split(RegExp('\\r?\\n')).map((item, key) => {
                                            return <span key={key}>{item}<br /></span>
                                        })}
                                    </Paragraph>
                                </Box>
                            }
                            <Box fill='horizontal' >
                                {this.renderLinks()}
                            </Box>
                        </Box>
                    }
                    {!isMax && ((video?.name === 'PLAYER_ERR_NOT_YET_AVAILABLE') || (video?.name === 'PLAYER_ERR_NO_LONGER_AVAILABLE')) &&
                        <Box gap="xsmall" pad={{ left: "xsmall", right: "xsmall", bottom: "small" }}>
                            <Box pad={{ bottom: "small" }}>
                                {(video?.name === 'PLAYER_ERR_NOT_YET_AVAILABLE') &&
                                    <Text weight='bold' size={(size === "medium_small") ? "small" : size}>Available Soon</Text>}
                                {(video?.name === 'PLAYER_ERR_NO_LONGER_AVAILABLE') &&
                                    <Text weight='bold' size={(size === "medium_small") ? "small" : size}>No  longer Available</Text>}
                            </Box>
                            <Box fill='horizontal'>
                                <Paragraph fill>
                                    Please view our related content until this video is active. Thanks for visiting <Anchor size="small" href="https://www.hpe.com" label="HPE.com" color="brand" />.
                                </Paragraph>
                            </Box>
                        </Box>
                    }
                </Box>
            }
        </Box>;
    }

    renderPlaylists = () => {
        let video = this.props.video.video;
        let searchParams = window.location.search;

        if (searchParams) {
            let matches = searchParams.match(/splistid=(\d+)/i);
            let playlistId = matches !== null ? matches[1] : "";
            let activeIndex = video?.relatedPlaylists.findIndex((playlist, index, arr) => { return playlist.id === playlistId });

            if (activeIndex !== -1) {
                this.setState({ activePlayList: activeIndex });
            }
        }
        return video && <Box fill gap="xsmall">
            <Accordion className="mt-2" activeIndex={this.state.activePlayList} onActive={activeIndexes => { this.onPlaylistChange(activeIndexes); }}>
                {
                    video.relatedPlaylists.map((list, index) =>
                        <AccordionPanel key={list.id} label={`${list.name} (${list.videos.length})`}>
                            <Box fill gap="small" pad="small" >
                                {
                                    list.videos.map((info, vindex) => {
                                        return <SummaryCard key={`playlist-video-${vindex}`} data={info} showTime showDescription showLanguage
                                            onClick={() => { this.gotoVideo(info) }} />;;
                                    })
                                }
                            </Box>
                        </AccordionPanel>
                    )}
            </Accordion>
        </Box>;
    }

    renderLinks = () => {
        let video = this.props.video.video;

        if (video && video.relatedLinks && video.relatedLinks.length) {
            return video.relatedLinks.map((link: RelatedLink, index: number) => {
                let location = window.location;
                let href = link.href
                    ? link.href.indexOf('http') !== -1 ? link.href : `${location.protocol}//${link.href}`
                    : "";

                return link.href && link.text
                    ? <Anchor key={`rlink-${index}`} size="small" href={`${href}`} label={link.text} color="brand" />
                    : link.href && !link.text
                        ? <Anchor key={`rlink-${index}`} size="small" href={`${href}`} label={href} color="brand" />
                        : <Text>{link.text}</Text>;
            }
            );
        }
        return null;
    }

    activateFirstTab = () => {
        let tabs = jQuery('.tab-pane');

        if (tabs.length !== 0 && !this.state.activeTabSet) {
            let firstItem = Number(jQuery(tabs[0]).attr('index'));

            this.setState({ activeTab: firstItem, activeTabSet: true });
        }
    }

    loadRecommendations = () => {
        let html = jQuery("#aiWidget").html();

        if (html !== '') {
            let $recommendations = jQuery(html).removeClass('psn-hidden').removeClass('psn-reduced')
                .addClass('psn-full').addClass('psn-static-mode').css('bottom', '0px');

            this.props.setRecommendationsLoaded(true);
            this.props.setRecommendations($recommendations[0].outerHTML);
        } else {
            setTimeout(() => {
                this.loadRecommendations();
            }, 500);
        }
    }

    renderTabs = () => {
        let video = this.props.video.video;
        let navs: any = [];
        let content: any = [];
        let isTest = isPreview();
        let isITG = isStaging();
        let isProd = isProduction();

        if (video && (video?.name !== 'NOT_AVAILABLE')) {
            if (video.relatedPlaylists && video.relatedPlaylists.length) {
                navs.push(
                    <NavItem key="related-playlists-tab">
                        <NavLink className={classnames({ active: this.state.activeTab === 1 })} onClick={() => { this.setActiveTab(1); }}>
                            Playlists
                        </NavLink>
                    </NavItem>
                );
                content.push(
                    <TabPane key="related-playlists-content" tabId="1" index="1">
                        {this.renderPlaylists()}
                    </TabPane>
                );
            }
            if (isTest || isITG || isProd) {
                if (video.recommendedTrackId) {
                    navs.push(
                        <NavItem key="pf-items-tab">
                            <NavLink className={classnames({ active: this.state.activeTab === 2 })} onClick={() => { this.setActiveTab(2); }}>
                                Recommended
                            </NavLink>
                        </NavItem>
                    );
                    content.push(
                        <TabPane key="pf-items-content" tabId="2" index="2">
                            <Box fill gap="small" pad="small" >
                                <div id="recommended" style={{ width: '100%', height: '100%' }}></div>
                            </Box>
                        </TabPane>
                    );
                }
                if (video.aiWidgetEnabled && video.uaid) {
                    if (!this.props.video.recommendationsLoading && !this.props.video.recommendationsLoaded) {
                        this.props.setRecommendationsLoading(true);
                        setTimeout(() => {
                            setupRecommendations(video);
                            jQuery(document).on("psnRecommendationsFinishedLoading", (event, data) => {
                                if (data.count === 0) {
                                    this.props.setAiWidget(false);
                                    this.setState({ activeTabSet: false });
                                }
                                else {
                                    this.loadRecommendations();
                                }
                                this.props.setRecommendationsLoading(false);
                            });
                        }, 500);
                    }
                    navs.push(
                        <NavItem key="ai-items-tab">
                            <NavLink className={classnames({ active: this.state.activeTab === 7 })} onClick={() => { this.setActiveTab(7); }}>
                                Recommended
                            </NavLink>
                        </NavItem>
                    );
                    content.push(
                        <TabPane key="ai-items-content" tabId="7" index="7">
                            <Box fill gap="small" pad="small" >
                                {!this.props.video.recommendationsLoaded && <Box justify="center" alignSelf="center" align="center" height="xxsmall" width="xsmall">
                                    <Image src="images/loader.svg" />
                                </Box>
                                }
                                <div id="aiWidget" style={{ width: '100%', height: '100%' }}>
                                    {this.props.video.recommendationsLoaded && this.props.video.recommendations
                                        ? parse(this.props.video.recommendations)
                                        : null}
                                </div>
                            </Box>
                        </TabPane>
                    );
                }
            }
        }
        if (video?.productLinks && video.productLinks.length && navs.length < 3) {
            navs.push(
                <NavItem key="productLinks-tab" >
                    <NavLink className={classnames({ active: this.state.activeTab === 3 })} onClick={() => { this.setActiveTab(3); }}>
                        HPE Store
                    </NavLink>
                </NavItem>
            );
            content.push(
                <TabPane key="productLinks-content" tabId="3" index="3">
                    <Box fill gap="small" pad="small" overflow={{ vertical: "auto", horizontal: "hidden" }}>
                        {video.productLinks.map((link, index) => {
                            return <SummaryCard key={`productLinks-video-${index}`} text={link.text} image={link.thumbnailImage}
                                height={{ max: '90px' }} onClick={() => { this.gotoUrl(link.href) }} />;
                        })}
                    </Box>
                </TabPane>
            );
        }
        if (video?.translations && video.translations.length && navs.length < 3) {
            navs.push(
                <NavItem key="translations-tab">
                    <NavLink className={classnames({ active: this.state.activeTab === 4 })} onClick={() => { this.setActiveTab(4); }}>
                        Translations
                    </NavLink>
                </NavItem>
            );
            content.push(
                <TabPane key="translations-content" tabId="4" index="4">
                    <Box fill gap="small" pad="small" overflow={{ vertical: "auto", horizontal: "hidden" }}>
                        {video.translations.map((info, index) => {
                            return <SummaryCard key={`translations-video-${index}`} data={info} showDescription showLanguage showTime
                                onClick={() => { this.gotoVideo(info) }} />;
                        })}
                    </Box>
                </TabPane>
            );
        }
        navs.push(
            <NavItem key="search-tab" >
                <NavLink className={classnames({ active: this.state.activeTab === 8 })} onClick={() => { this.setActiveTab(8); }}>
                    Search
                </NavLink>
            </NavItem>
        );
        content.push(
            <TabPane key="search-content" tabId="8" index="8">
                <Box gap="small" pad="small" overflow={{ vertical: "auto", horizontal: "hidden" }} >
                    <Box direction='row' fill="horizontal">
                        <TextInput placeholder='Search more videos ...' value={this.props.search?.request.keyword} id="search-keyword"
                            ref={this.searchInput} onChange={this.onKeywordChange} />
                        <Button icon={<Search />} onClick={() => { this.performSearch(); }} />
                    </Box>
                    <Box direction='row' justify="end">
                        <Anchor size="xsmall" onClick={async () => {
                            const searchParams = new URLSearchParams(window.location.search);
                            const lang = searchParams.get("lang");
                            const path =  "/video-gallery";
                            const rd = null;
                            const redirected = await checkRedirect({path,rd,lang});
                            if (!redirected) {
                                this.performAdvancedSearch();
                            }
                        }} label="Advanced Search" />
                    </Box>
                    <Box fill gap="small" pad="xsmall" overflow={{ vertical: "auto", horizontal: "hidden" }}>
                        {_.map(this.props.search?.results.videos, (info, index) => {
                            return <SummaryCard key={`search-resutls-${index}`} data={info} showDescription showLanguage showTime
                                onClick={() => { this.gotoVideo(info) }} />;
                        })}
                    </Box>
                    {this.props.search.loading && <Box justify="center" alignSelf="center" align="center" height="xxsmall" width="xsmall">
                        <Image src="images/loader.svg" />
                    </Box>}
                </Box>
            </TabPane>
        );

        setTimeout(() => {
            this.activateFirstTab();
        }, 250);
        return <Box fill>
            <Nav tabs>
                {navs}
            </Nav>
            <TabContent activeTab={`${this.state.activeTab}`} className="tab-content-container">
                {content}
            </TabContent>
        </Box>;
    }

    public render() {
        let isMax = this.props.match.params.max !== undefined && this.props.match.params.max === 'max';
        let desktopSizes = ["medium", "medium_large", "large", "xlarge"];

        if (isMax) {
            jQuery("#header").hide();
            jQuery("#footer").hide();
            jQuery("#content-body").removeClass("hpehf-centered-content")
        }
        return <ResponsiveContext.Consumer>
            {size => (
                <Box fill >
                    {this.props.video.loading
                        ? <Layer background="none">
                            <Box align="center" justify="center" height="xxsmall" width="xsmall">
                                <Image src="images/loader.svg" />
                            </Box>
                        </Layer>
                        : <Box fill>
                            {(desktopSizes.indexOf(size) === -1)
                                ? <Box fill gap="small">
                                    <Box>
                                        {this.renderVideo(isMax, size)}
                                    </Box>
                                    {!isMax && <Box>
                                        {this.renderTabs()}
                                    </Box>}
                                </Box>
                                : <Box fill direction="row" pad={{ top: "small" }} >
                                    <Box gap="none" width="75%">
                                        {this.renderVideo(isMax, size)}
                                    </Box>
                                    {!isMax && <Box width="25%" gap="xsmall" pad={{ left: "small" }} >
                                        {this.renderTabs()}
                                    </Box>}
                                </Box>
                            }
                        </Box>}
                </Box>
            )}
        </ResponsiveContext.Consumer>;
    }
}

export default withRouter(connect(
    (state: ApplicationState) => state, // Selects which state properties are merged into the component's props
    mapDispatchToProps)(VideoPage as any));
